import { action, observable, makeObservable, runInAction } from 'mobx';
import { EntityName, get, post } from 'stores/_services/dataService';

interface IAutomaticFileAnalysisSettings {
    enabled: boolean;
    sizeThreshold: number;
}

interface IUninstallProtectionSettings {
    enabled: boolean;
    token: string;
}

export interface IScheduleOptions {
    options: {
        hours?: string;
        ampm?: string;
        expression?: string;
        useLocalTimezone?: boolean;
    };
    cron: string;
}

export default class SettingsStore {
    dashboardLastUpdated = null;
    dashboardMinRefreshInterval = 5;
    dashboardUpdateStatus = 'complete';
    restrictNonUsPersonnel = false;
    monitoringScheduledScanCron = '0 16 * * *';
    snDeviceOfflineEnabled = false;
    snDeviceIsolatedEnabled = false;
    snDeviceDisabled = false;
    snApiTokenExpiredEnabled = false;

    automaticFileAnalysisSettings: IAutomaticFileAnalysisSettings = {
        enabled: false,
        sizeThreshold: 0.5
    };

    uninstallProtectionSettings: IUninstallProtectionSettings = {
        enabled: false,
        token: ''
    };

    constructor() {
        makeObservable(this, {
            dashboardLastUpdated: observable,
            dashboardMinRefreshInterval: observable,
            dashboardUpdateStatus: observable,
            restrictNonUsPersonnel: observable,
            monitoringScheduledScanCron: observable,
            automaticFileAnalysisSettings: observable,
            uninstallProtectionSettings: observable,
            snDeviceOfflineEnabled: observable,
            snDeviceIsolatedEnabled: observable,
            snApiTokenExpiredEnabled: observable,
            getAutomaticFileAnalysisSettings: action,
            getMonitoringScheduledScanCron: action,
            getRestrictNonUsPersonnel: action,
            getUninstallProtectionSettings: action,
            updateRestrictNonUsPersonnel: action,
            updateAutomaticFileAnalysis: action,
            updateUninstallProtection: action
        });
    }

    async getAutomaticFileAnalysisSettings(): Promise<void> {
        const { data } = await get(`${EntityName.SETTINGS}/getAutomaticFileAnalysisSettings`);
        runInAction(() => {
            this.automaticFileAnalysisSettings = data;
        });
    }

    async getMonitoringScheduledScanCron(): Promise<void> {
        const { data } = await get(`${EntityName.SETTINGS}/getMonitoringScheduledScanCron`);
        runInAction(() => {
            this.monitoringScheduledScanCron = data;
        });
    }

    async getDashboardLastUpdated(): Promise<string> {
        const { data } = await get(`${EntityName.SETTINGS}/getDashboardLastUpdated`);
        runInAction(() => {
            this.dashboardLastUpdated = data.dashboardLastUpdated;
        });
        return data.dashboardLastUpdated;
    }

    async getDashboardMinRefreshInterval(): Promise<number> {
        const { data } = await get(`${EntityName.SETTINGS}/getDashboardMinRefreshInterval`);
        runInAction(() => {
            this.dashboardMinRefreshInterval = data.dashboardMinRefreshInterval;
        });
        return data.dashboardMinRefreshInterval;
    }

    async getDashboardUpdateStatus(): Promise<string> {
        const { data } = await get(`${EntityName.SETTINGS}/getDashboardUpdateStatus`);
        runInAction(() => {
            this.dashboardUpdateStatus = data.dashboardUpdateStatus;
        });
        return data.dashboardUpdateStatus;
    }

    async setDashboardUpdateStatus(status: string): Promise<void> {
        await post(`${EntityName.SETTINGS}/setDashboardUpdateStatus`, { status });
        runInAction(() => {
            this.dashboardUpdateStatus = status;
        });
    }

    async getRestrictNonUsPersonnel(): Promise<void> {
        const { data } = await get(`${EntityName.SETTINGS}/getRestrictNonUsPersonnel`);
        runInAction(() => {
            this.restrictNonUsPersonnel = data.restrictNonUsPersonnel;
        });
    }

    async updateRestrictNonUsPersonnel(enabled: boolean): Promise<void> {
        await post(`${EntityName.SETTINGS}/updateRestrictNonUsPersonnel`, { enabled });
        runInAction(() => {
            this.restrictNonUsPersonnel = enabled;
        });
    }

    async getRemoveInactiveAgents(): Promise<{ removeInactiveAgents?: number }> {
        const { data } = await get(`${EntityName.SETTINGS}/getRemoveInactiveAgents`);

        return data;
    }

    async updateRemoveInactiveAgents(removeInactiveAgents: number | null): Promise<void> {
        await post(`${EntityName.SETTINGS}/updateRemoveInactiveAgents`, { removeInactiveAgents });
    }

    async updateAutomaticFileAnalysis({ enabled, sizeThreshold }: IAutomaticFileAnalysisSettings): Promise<void> {
        const payload = {
            enabled,
            sizeThreshold
        };

        await post(`${EntityName.SETTINGS}/updateAutomaticFileAnalysis`, payload);
        runInAction(() => {
            this.automaticFileAnalysisSettings.sizeThreshold = sizeThreshold;
            this.automaticFileAnalysisSettings.enabled = enabled;
        });
    }

    async updateUninstallProtection({ enabled, token }: IUninstallProtectionSettings): Promise<void> {
        const payload = {
            enabled,
            token
        };

        await post(`${EntityName.SETTINGS}/updateUninstallProtection`, payload);
        runInAction(() => {
            this.uninstallProtectionSettings.enabled = enabled;
            this.uninstallProtectionSettings.token = token;
        });
    }

    async getUninstallProtectionSettings(): Promise<void> {
        const { data } = await get(`${EntityName.SETTINGS}/getUninstallProtectionSettings`);
        runInAction(() => {
            this.uninstallProtectionSettings = data;
        });
    }

    async getSystemNotificationSettings() {
        const { data } = await get(`${EntityName.SETTINGS}/getSystemNotificationSettings`);
        runInAction(() => {
            this.snDeviceOfflineEnabled = data.deviceOfflineEnabled;
            this.snDeviceIsolatedEnabled = data.deviceIsolatedEnabled;
            this.snDeviceDisabled = data.deviceDisabled;
            this.snApiTokenExpiredEnabled = data.apiTokenExpiredEnabled;
        });
    }

    async setApiTokenExpiredEnabledSetting(enabled: boolean): Promise<void> {
        const payload = {
            enabled
        };

        await post(`${EntityName.SETTINGS}/setApiTokenExpiredEnabledSetting`, payload);
        runInAction(() => {
            this.snApiTokenExpiredEnabled = enabled;
        });
    }

    async setDeviceIsolatedEnabledSetting(enabled: boolean): Promise<void> {
        const payload = {
            enabled
        };

        await post(`${EntityName.SETTINGS}/setDeviceIsolatedEnabledSetting`, payload);
        runInAction(() => {
            this.snDeviceIsolatedEnabled = enabled;
        });
    }

    async setDeviceOfflineEnabledSetting(enabled: boolean): Promise<void> {
        const payload = {
            enabled
        };

        await post(`${EntityName.SETTINGS}/setDeviceOfflineEnabledSetting`, payload);
        runInAction(() => {
            this.snDeviceOfflineEnabled = enabled;
        });
    }

    async setDeviceDisabledSetting(disabled: boolean): Promise<void> {
        const payload = {
            disabled
        };

        await post(`${EntityName.SETTINGS}/setDeviceDisabledSetting`, payload);
        runInAction(() => {
            this.snDeviceDisabled = disabled;
        });
    }
}
